export const CHANNELS = [
  "Instagram",
  "Instagram Ads",
  "Facebook",
  "Facebook Ads",
  "Web Analytics (GA4)",
  "Google Ads",
] as const;

export type Channel = (typeof CHANNELS)[number];

export const CHANNEL_COLORS: Record<Channel, string> = {
  Instagram: "hsl(328, 85%, 55%)",
  "Instagram Ads": "hsl(280, 70%, 55%)",
  Facebook: "hsl(217, 91%, 60%)",
  "Facebook Ads": "hsl(210, 78%, 45%)",
  "Web Analytics (GA4)": "hsl(25, 95%, 53%)",
  "Google Ads": "hsl(142, 71%, 45%)",
};

export const ATTRIBUTION_MODELS = [
  "First-Touch",
  "Last-Touch",
  "Multi-Touch",
  "Time Decay",
] as const;
export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number];

export type TimePeriod = "7d" | "30d" | "90d" | "12m";
export type ChannelType = "all" | "paid" | "organic";

export type ChannelKey =
  | "instagram"
  | "instagramAds"
  | "facebook"
  | "facebookAds"
  | "webAnalyticsGa4"
  | "googleAds";

export const CHANNEL_KEY_MAP: Record<Channel, ChannelKey> = {
  Instagram: "instagram",
  "Instagram Ads": "instagramAds",
  Facebook: "facebook",
  "Facebook Ads": "facebookAds",
  "Web Analytics (GA4)": "webAnalyticsGa4",
  "Google Ads": "googleAds",
};

export interface WeeklyData {
  week: string;
  instagram: number;
  instagramAds: number;
  facebook: number;
  facebookAds: number;
  webAnalyticsGa4: number;
  googleAds: number;
}

export const weeklyConversions: WeeklyData[] = [
  { week: "W1", instagram: 35, instagramAds: 88, facebook: 42, facebookAds: 95, webAnalyticsGa4: 30, googleAds: 90 },
  { week: "W2", instagram: 40, instagramAds: 95, facebook: 48, facebookAds: 105, webAnalyticsGa4: 38, googleAds: 102 },
  { week: "W3", instagram: 45, instagramAds: 102, facebook: 55, facebookAds: 112, webAnalyticsGa4: 42, googleAds: 98 },
  { week: "W4", instagram: 38, instagramAds: 118, facebook: 50, facebookAds: 125, webAnalyticsGa4: 48, googleAds: 115 },
  { week: "W5", instagram: 52, instagramAds: 108, facebook: 60, facebookAds: 110, webAnalyticsGa4: 35, googleAds: 125 },
  { week: "W6", instagram: 48, instagramAds: 125, facebook: 58, facebookAds: 135, webAnalyticsGa4: 52, googleAds: 130 },
  { week: "W7", instagram: 55, instagramAds: 138, facebook: 65, facebookAds: 142, webAnalyticsGa4: 45, googleAds: 145 },
  { week: "W8", instagram: 50, instagramAds: 130, facebook: 55, facebookAds: 128, webAnalyticsGa4: 55, googleAds: 138 },
];

export const weeklyRevenue: WeeklyData[] = [
  { week: "W1", instagram: 2450, instagramAds: 8800, facebook: 2940, facebookAds: 9500, webAnalyticsGa4: 2400, googleAds: 10800 },
  { week: "W2", instagram: 2800, instagramAds: 9500, facebook: 3360, facebookAds: 10500, webAnalyticsGa4: 3040, googleAds: 12240 },
  { week: "W3", instagram: 3150, instagramAds: 10200, facebook: 3850, facebookAds: 11200, webAnalyticsGa4: 3360, googleAds: 11760 },
  { week: "W4", instagram: 2660, instagramAds: 11800, facebook: 3500, facebookAds: 12500, webAnalyticsGa4: 3840, googleAds: 13800 },
  { week: "W5", instagram: 3640, instagramAds: 10800, facebook: 4200, facebookAds: 11000, webAnalyticsGa4: 2800, googleAds: 15000 },
  { week: "W6", instagram: 3360, instagramAds: 12500, facebook: 4060, facebookAds: 13500, webAnalyticsGa4: 4160, googleAds: 15600 },
  { week: "W7", instagram: 3850, instagramAds: 13800, facebook: 4550, facebookAds: 14200, webAnalyticsGa4: 3600, googleAds: 17400 },
  { week: "W8", instagram: 3500, instagramAds: 13000, facebook: 3850, facebookAds: 12800, webAnalyticsGa4: 4400, googleAds: 16560 },
];

export interface AttributionData {
  model: AttributionModel;
  instagram: number;
  instagramAds: number;
  facebook: number;
  facebookAds: number;
  webAnalyticsGa4: number;
  googleAds: number;
}

export const attributionModelData: AttributionData[] = [
  { model: "First-Touch", instagram: 145, instagramAds: 210, facebook: 160, facebookAds: 230, webAnalyticsGa4: 125, googleAds: 250 },
  { model: "Last-Touch", instagram: 60, instagramAds: 310, facebook: 70, facebookAds: 340, webAnalyticsGa4: 55, googleAds: 320 },
  { model: "Multi-Touch", instagram: 110, instagramAds: 260, facebook: 120, facebookAds: 280, webAnalyticsGa4: 100, googleAds: 285 },
  { model: "Time Decay", instagram: 85, instagramAds: 285, facebook: 95, facebookAds: 305, webAnalyticsGa4: 80, googleAds: 300 },
];

export interface FunnelStage {
  stage: string;
  instagram: number;
  instagramAds: number;
  facebook: number;
  facebookAds: number;
  webAnalyticsGa4: number;
  googleAds: number;
}

export const funnelData: FunnelStage[] = [
  { stage: "Impressions", instagram: 520000, instagramAds: 680000, facebook: 410000, facebookAds: 720000, webAnalyticsGa4: 280000, googleAds: 650000 },
  { stage: "Clicks", instagram: 26000, instagramAds: 34000, facebook: 20500, facebookAds: 50400, webAnalyticsGa4: 33600, googleAds: 45500 },
  { stage: "Conversions", instagram: 363, instagramAds: 904, facebook: 433, facebookAds: 952, webAnalyticsGa4: 345, googleAds: 943 },
];

export interface FlowData {
  source: string;
  target: string;
  value: number;
}

export const channelFlowData: FlowData[] = [
  { source: "Instagram", target: "Instagram Ads", value: 2800 },
  { source: "Instagram", target: "Facebook Ads", value: 1200 },
  { source: "Instagram", target: "Conversion", value: 363 },
  { source: "Facebook", target: "Facebook Ads", value: 3100 },
  { source: "Facebook", target: "Google Ads", value: 1500 },
  { source: "Facebook", target: "Conversion", value: 433 },
  { source: "Web Analytics (GA4)", target: "Google Ads", value: 2400 },
  { source: "Web Analytics (GA4)", target: "Instagram Ads", value: 1000 },
  { source: "Web Analytics (GA4)", target: "Conversion", value: 345 },
  { source: "Instagram Ads", target: "Conversion", value: 904 },
  { source: "Facebook Ads", target: "Conversion", value: 952 },
  { source: "Google Ads", target: "Conversion", value: 943 },
];

export interface MonthlyTimeSeries {
  month: string;
  instagramConv: number;
  instagramAdsConv: number;
  facebookConv: number;
  facebookAdsConv: number;
  webGa4Conv: number;
  googleAdsConv: number;
  instagramRev: number;
  instagramAdsRev: number;
  facebookRev: number;
  facebookAdsRev: number;
  webGa4Rev: number;
  googleAdsRev: number;
}

export const monthlyTimeSeries: MonthlyTimeSeries[] = [
  { month: "Jan", instagramConv: 140, instagramAdsConv: 350, facebookConv: 170, facebookAdsConv: 380, webGa4Conv: 120, googleAdsConv: 360, instagramRev: 9800, instagramAdsRev: 35000, facebookRev: 11900, facebookAdsRev: 38000, webGa4Rev: 9600, googleAdsRev: 43200 },
  { month: "Feb", instagramConv: 155, instagramAdsConv: 380, facebookConv: 185, facebookAdsConv: 400, webGa4Conv: 135, googleAdsConv: 390, instagramRev: 10850, instagramAdsRev: 38000, facebookRev: 12950, facebookAdsRev: 40000, webGa4Rev: 10800, googleAdsRev: 46800 },
  { month: "Mar", instagramConv: 180, instagramAdsConv: 420, facebookConv: 215, facebookAdsConv: 450, webGa4Conv: 150, googleAdsConv: 430, instagramRev: 12600, instagramAdsRev: 42000, facebookRev: 15050, facebookAdsRev: 45000, webGa4Rev: 12000, googleAdsRev: 51600 },
  { month: "Apr", instagramConv: 170, instagramAdsConv: 400, facebookConv: 200, facebookAdsConv: 430, webGa4Conv: 145, googleAdsConv: 445, instagramRev: 11900, instagramAdsRev: 40000, facebookRev: 14000, facebookAdsRev: 43000, webGa4Rev: 11600, googleAdsRev: 53400 },
  { month: "May", instagramConv: 200, instagramAdsConv: 450, facebookConv: 240, facebookAdsConv: 480, webGa4Conv: 170, googleAdsConv: 470, instagramRev: 14000, instagramAdsRev: 45000, facebookRev: 16800, facebookAdsRev: 48000, webGa4Rev: 13600, googleAdsRev: 56400 },
  { month: "Jun", instagramConv: 220, instagramAdsConv: 490, facebookConv: 260, facebookAdsConv: 520, webGa4Conv: 185, googleAdsConv: 500, instagramRev: 15400, instagramAdsRev: 49000, facebookRev: 18200, facebookAdsRev: 52000, webGa4Rev: 14800, googleAdsRev: 60000 },
];

export interface CostRevenueData {
  channel: Channel;
  cost: number;
  revenue: number;
  roas: number;
}

export const costRevenueData: CostRevenueData[] = [
  { channel: "Instagram", cost: 4200, revenue: 25410, roas: 6.05 },
  { channel: "Instagram Ads", cost: 32000, revenue: 90400, roas: 2.83 },
  { channel: "Facebook", cost: 3800, revenue: 28310, roas: 7.45 },
  { channel: "Facebook Ads", cost: 42000, revenue: 95200, roas: 2.27 },
  { channel: "Web Analytics (GA4)", cost: 3200, revenue: 24960, roas: 7.8 },
  { channel: "Google Ads", cost: 48000, revenue: 113160, roas: 2.36 },
];

export interface HeatmapCell {
  source: Channel;
  target: Channel;
  impact: number;
}

export const heatmapData: HeatmapCell[] = CHANNELS.flatMap(source =>
  CHANNELS.map(target => ({
    source,
    target,
    impact: source === target ? 80 : 40,
  }))
);

const heatSeed: Record<string, number> = {
  "Instagram-Instagram": 82,
  "Instagram-Instagram Ads": 75,
  "Instagram-Facebook": 65,
  "Instagram-Facebook Ads": 52,
  "Instagram-Web Analytics (GA4)": 28,
  "Instagram-Google Ads": 35,
  "Instagram Ads-Instagram": 70,
  "Instagram Ads-Instagram Ads": 88,
  "Instagram Ads-Facebook": 42,
  "Instagram Ads-Facebook Ads": 58,
  "Instagram Ads-Web Analytics (GA4)": 32,
  "Instagram Ads-Google Ads": 48,
  "Facebook-Instagram": 60,
  "Facebook-Instagram Ads": 45,
  "Facebook-Facebook": 85,
  "Facebook-Facebook Ads": 72,
  "Facebook-Web Analytics (GA4)": 38,
  "Facebook-Google Ads": 40,
  "Facebook Ads-Instagram": 48,
  "Facebook Ads-Instagram Ads": 55,
  "Facebook Ads-Facebook": 68,
  "Facebook Ads-Facebook Ads": 90,
  "Facebook Ads-Web Analytics (GA4)": 42,
  "Facebook Ads-Google Ads": 55,
  "Web Analytics (GA4)-Instagram": 30,
  "Web Analytics (GA4)-Instagram Ads": 35,
  "Web Analytics (GA4)-Facebook": 32,
  "Web Analytics (GA4)-Facebook Ads": 45,
  "Web Analytics (GA4)-Web Analytics (GA4)": 78,
  "Web Analytics (GA4)-Google Ads": 62,
  "Google Ads-Instagram": 38,
  "Google Ads-Instagram Ads": 50,
  "Google Ads-Facebook": 35,
  "Google Ads-Facebook Ads": 52,
  "Google Ads-Web Analytics (GA4)": 55,
  "Google Ads-Google Ads": 92,
};

heatmapData.forEach(cell => {
  const key = `${cell.source}-${cell.target}`;
  if (heatSeed[key] !== undefined) {
    cell.impact = heatSeed[key];
  }
});

export interface CustomerInteraction {
  customerId: string;
  channel: Channel;
  timestamp: string;
  touchpoint: "Impression" | "Click" | "Conversion";
  conversionStatus: "Converted" | "Not Converted";
  revenue: number | null;
}

export const customerInteractions: CustomerInteraction[] = [
  { customerId: "CUS-10482", channel: "Instagram", timestamp: "2024-03-01 09:14", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10482", channel: "Instagram Ads", timestamp: "2024-03-02 14:22", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10482", channel: "Google Ads", timestamp: "2024-03-05 11:08", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 124 },
  { customerId: "CUS-10483", channel: "Facebook", timestamp: "2024-03-01 10:30", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10483", channel: "Facebook Ads", timestamp: "2024-03-03 16:45", touchpoint: "Click", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10484", channel: "Web Analytics (GA4)", timestamp: "2024-03-02 08:05", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10484", channel: "Google Ads", timestamp: "2024-03-03 12:18", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10484", channel: "Facebook Ads", timestamp: "2024-03-06 09:42", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 89 },
  { customerId: "CUS-10485", channel: "Instagram Ads", timestamp: "2024-03-01 11:20", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10485", channel: "Instagram Ads", timestamp: "2024-03-04 15:33", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10485", channel: "Instagram Ads", timestamp: "2024-03-07 10:15", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 210 },
  { customerId: "CUS-10486", channel: "Facebook", timestamp: "2024-03-02 13:40", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10487", channel: "Google Ads", timestamp: "2024-03-01 07:55", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10487", channel: "Instagram", timestamp: "2024-03-03 18:22", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10487", channel: "Google Ads", timestamp: "2024-03-08 14:10", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 156 },
  { customerId: "CUS-10488", channel: "Facebook Ads", timestamp: "2024-03-02 09:15", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10488", channel: "Web Analytics (GA4)", timestamp: "2024-03-04 11:48", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10488", channel: "Facebook Ads", timestamp: "2024-03-09 08:30", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 78 },
  { customerId: "CUS-10489", channel: "Instagram", timestamp: "2024-03-03 10:05", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10489", channel: "Instagram Ads", timestamp: "2024-03-05 14:30", touchpoint: "Click", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10490", channel: "Google Ads", timestamp: "2024-03-01 16:20", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10490", channel: "Google Ads", timestamp: "2024-03-04 09:45", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10490", channel: "Google Ads", timestamp: "2024-03-10 13:12", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 195 },
  { customerId: "CUS-10491", channel: "Web Analytics (GA4)", timestamp: "2024-03-02 15:30", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10491", channel: "Instagram Ads", timestamp: "2024-03-05 10:18", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10491", channel: "Instagram Ads", timestamp: "2024-03-08 16:55", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 142 },
  { customerId: "CUS-10492", channel: "Facebook Ads", timestamp: "2024-03-03 08:42", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10493", channel: "Instagram", timestamp: "2024-03-01 12:10", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10493", channel: "Facebook Ads", timestamp: "2024-03-04 17:25", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10493", channel: "Google Ads", timestamp: "2024-03-07 11:40", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 168 },
  { customerId: "CUS-10494", channel: "Facebook", timestamp: "2024-03-02 14:55", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10494", channel: "Facebook", timestamp: "2024-03-05 09:30", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10494", channel: "Facebook Ads", timestamp: "2024-03-09 15:20", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 95 },
  { customerId: "CUS-10495", channel: "Google Ads", timestamp: "2024-03-03 11:15", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10495", channel: "Instagram", timestamp: "2024-03-06 08:50", touchpoint: "Click", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10496", channel: "Instagram Ads", timestamp: "2024-03-01 15:40", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10496", channel: "Web Analytics (GA4)", timestamp: "2024-03-04 13:05", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10496", channel: "Instagram Ads", timestamp: "2024-03-08 10:35", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 183 },
  { customerId: "CUS-10497", channel: "Facebook Ads", timestamp: "2024-03-02 07:20", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10497", channel: "Google Ads", timestamp: "2024-03-05 16:10", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10497", channel: "Google Ads", timestamp: "2024-03-10 09:28", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 221 },
  { customerId: "CUS-10498", channel: "Instagram", timestamp: "2024-03-03 14:35", touchpoint: "Impression", conversionStatus: "Not Converted", revenue: null },
  { customerId: "CUS-10499", channel: "Web Analytics (GA4)", timestamp: "2024-03-01 09:50", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10499", channel: "Facebook", timestamp: "2024-03-03 12:45", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10499", channel: "Facebook Ads", timestamp: "2024-03-07 14:58", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 112 },
  { customerId: "CUS-10500", channel: "Google Ads", timestamp: "2024-03-02 10:25", touchpoint: "Impression", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10500", channel: "Instagram Ads", timestamp: "2024-03-05 08:15", touchpoint: "Click", conversionStatus: "Converted", revenue: null },
  { customerId: "CUS-10500", channel: "Instagram Ads", timestamp: "2024-03-09 11:42", touchpoint: "Conversion", conversionStatus: "Converted", revenue: 134 },
];

export const PAID_CHANNELS: Channel[] = ["Instagram Ads", "Facebook Ads", "Google Ads"];
export const ORGANIC_CHANNELS: Channel[] = ["Instagram", "Facebook", "Web Analytics (GA4)"];

export function getVisibleChannels(channelType: ChannelType): Channel[] {
  if (channelType === "paid") {
    return PAID_CHANNELS;
  }
  if (channelType === "organic") {
    return ORGANIC_CHANNELS;
  }
  return [...CHANNELS];
}

function sliceByTime<T>(data: T[], timePeriod: TimePeriod): T[] {
  if (timePeriod === "7d") {
    return data.slice(0, 2);
  }
  if (timePeriod === "30d") {
    return data.slice(0, 4);
  }
  if (timePeriod === "90d") {
    return data.slice(0, 6);
  }
  return data;
}

export function getFilteredWeeklyConversions(timePeriod: TimePeriod) {
  return sliceByTime(weeklyConversions, timePeriod);
}

export function getFilteredWeeklyRevenue(timePeriod: TimePeriod) {
  return sliceByTime(weeklyRevenue, timePeriod);
}

export function getFilteredTimeSeries(timePeriod: TimePeriod) {
  return sliceByTime(monthlyTimeSeries, timePeriod);
}

export function getFilteredCostRevenue(channelType: ChannelType) {
  const visible = getVisibleChannels(channelType);
  return costRevenueData.filter(d => visible.includes(d.channel));
}

export function getFilteredHeatmap(channelType: ChannelType) {
  const visible = getVisibleChannels(channelType);
  return heatmapData.filter(
    d => visible.includes(d.source) && visible.includes(d.target)
  );
}

export function getFilteredFlowData(channelType: ChannelType) {
  const visible = getVisibleChannels(channelType);
  return channelFlowData.filter(d => {
    const srcOk = visible.includes(d.source as Channel) || d.source === "Conversion";
    const tgtOk = visible.includes(d.target as Channel) || d.target === "Conversion";
    return srcOk && tgtOk;
  });
}

export function getFilteredFunnel(timePeriod: TimePeriod, channelType: ChannelType) {
  const scale =
    timePeriod === "7d" ? 0.25 : timePeriod === "30d" ? 0.5 : timePeriod === "90d" ? 0.75 : 1;
  const visible = getVisibleChannels(channelType);
  return funnelData.map(stage => ({
    ...stage,
    instagram: visible.includes("Instagram") ? Math.round(stage.instagram * scale) : 0,
    instagramAds: visible.includes("Instagram Ads") ? Math.round(stage.instagramAds * scale) : 0,
    facebook: visible.includes("Facebook") ? Math.round(stage.facebook * scale) : 0,
    facebookAds: visible.includes("Facebook Ads") ? Math.round(stage.facebookAds * scale) : 0,
    webAnalyticsGa4: visible.includes("Web Analytics (GA4)") ? Math.round(stage.webAnalyticsGa4 * scale) : 0,
    googleAds: visible.includes("Google Ads") ? Math.round(stage.googleAds * scale) : 0,
  }));
}

export function getFilteredCustomerInteractions(channelType: ChannelType): CustomerInteraction[] {
  const visible = getVisibleChannels(channelType);
  return customerInteractions.filter(i => visible.includes(i.channel));
}

export const getKPISummary = (timePeriod: TimePeriod, channelType: ChannelType) => {
  const convData = getFilteredWeeklyConversions(timePeriod);
  const revData = getFilteredWeeklyRevenue(timePeriod);
  const visible = getVisibleChannels(channelType);
  const keys = visible.map(ch => CHANNEL_KEY_MAP[ch]);

  const totalConversions = convData.reduce(
    (sum, w) => sum + keys.reduce((s, k) => s + w[k], 0),
    0
  );
  const totalRevenue = revData.reduce(
    (sum, w) => sum + keys.reduce((s, k) => s + w[k], 0),
    0
  );
  const costData = getFilteredCostRevenue(channelType);
  const totalCost = costData.reduce((sum, d) => sum + d.cost, 0);
  const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0;

  const channelTotals = visible.map(ch => ({
    channel: ch,
    conversions: convData.reduce((s, w) => s + w[CHANNEL_KEY_MAP[ch]], 0),
  }));
  const topChannel =
    channelTotals.length > 0
      ? channelTotals.sort((a, b) => b.conversions - a.conversions)[0].channel
      : "N/A";

  return { totalConversions, totalRevenue, avgRoas, topChannel };
};
